import React, { useState } from 'react';
import { Loader2, CheckCircle2, XCircle, TestTube2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Standalone "Test URL" widget for admins to check an arbitrary stream URL
 * BEFORE adding it to the system. Does not save anything — purely validation.
 */
const ERROR_HINTS = {
  524: 'Cloudflare 524: Ο server του σταθμού δεν απάντησε εντός χρόνου. Δοκίμασε ξανά σε λίγα λεπτά ή επικοινώνησε με τον πάροχο του stream.',
  522: 'Cloudflare 522: Αποτυχία σύνδεσης με τον origin server.',
  502: 'Bad Gateway: Πρόβλημα μεταξύ του proxy και του stream server.',
  503: 'Service Unavailable: Ο stream server είναι προσωρινά εκτός.',
  404: 'Not Found: Το URL δεν υπάρχει σε αυτόν τον server.',
  401: 'Unauthorized: Το stream απαιτεί authentication.',
  403: 'Forbidden: Ο server αρνήθηκε την πρόσβαση.',
};

const sanitizeError = (raw) => {
  if (!raw) return 'Μη έγκυρο URL';
  const text = String(raw);
  // Detect Cloudflare / generic HTML error pages and extract a short summary
  if (/<!DOCTYPE html|<html/i.test(text)) {
    const codeMatch = text.match(/Error code\s*(\d{3})|(\d{3})\s*error|status[-_ ]?code[^0-9]*(\d{3})/i);
    const code = Number(codeMatch?.[1] || codeMatch?.[2] || codeMatch?.[3]);
    const titleMatch = text.match(/<title>([^<]{3,120})<\/title>/i);
    const cfDetected = /cloudflare/i.test(text);
    const hint = ERROR_HINTS[code] || (cfDetected ? 'Ο Cloudflare proxy απέρριψε την σύνδεση με τον server του stream.' : null);
    if (code && hint) return `HTTP ${code} — ${hint}`;
    if (code) return `HTTP ${code} — ο server επέστρεψε HTML error page (πιθανό downtime ή misconfig).`;
    if (titleMatch) return `HTTP error — ${titleMatch[1].trim()}`;
    return 'Ο server επέστρεψε HTML αντί για stream (πιθανόν offline ή λάθος URL).';
  }
  // Trim long plain-text errors
  if (text.length > 300) return text.slice(0, 280) + '…';
  return text;
};

const QuickTestUrl = ({ apiBase, token }) => {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const handleTest = () => {
    if (!url.trim()) {
      toast.error('Εισάγετε URL');
      return;
    }
    setLoading(true);
    setResult(null);
    const xhr = new XMLHttpRequest();
    xhr.open('GET', `${apiBase}/api/stream-monitor/test-url?url=${encodeURIComponent(url.trim())}`, true);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.onload = () => {
      let data = {};
      try { data = JSON.parse(xhr.responseText); } catch { data = { detail: xhr.responseText }; }
      setLoading(false);
      if (xhr.status >= 200 && xhr.status < 300) {
        setResult({ ok: true, metadata: data.metadata, contentType: data.content_type });
        toast.success('URL έγκυρο!');
      } else {
        const cleaned = sanitizeError(data.detail || xhr.responseText || 'Μη έγκυρο URL');
        setResult({ ok: false, error: cleaned, status: xhr.status });
        toast.error(cleaned.length > 120 ? cleaned.slice(0, 120) + '…' : cleaned);
      }
    };
    xhr.onerror = () => {
      setLoading(false);
      setResult({ ok: false, error: 'Σφάλμα δικτύου' });
      toast.error('Σφάλμα δικτύου');
    };
    xhr.send();
  };

  if (!expanded) {
    return (
      <button
        data-testid="quick-test-url-toggle"
        onClick={() => setExpanded(true)}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-indigo-500/15 hover:bg-indigo-500/25 border border-indigo-500/40 text-indigo-300 text-xs font-medium transition-colors"
      >
        <TestTube2 className="w-3.5 h-3.5" />
        Test Stream URL
      </button>
    );
  }

  return (
    <div data-testid="quick-test-url" className="bg-indigo-500/5 border border-indigo-500/30 rounded-lg p-3 mb-3">
      <div className="flex items-center gap-2 mb-2 text-xs text-indigo-300 font-medium">
        <TestTube2 className="w-4 h-4" />
        Γρήγορος έλεγχος Stream URL
        <span className="text-gray-500 font-normal ml-2">— δοκιμάστε αν ένα stream URL δουλεύει πριν το προσθέσετε</span>
      </div>
      <div className="flex gap-2">
        <input
          data-testid="quick-test-url-input"
          type="url"
          placeholder="https://stream.example.com/live"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleTest()}
          className="flex-1 bg-gray-900 border border-white/10 rounded-md px-3 py-1.5 text-sm text-gray-100 placeholder:text-gray-600 focus:outline-none focus:border-indigo-400"
        />
        <button
          data-testid="quick-test-url-submit"
          onClick={handleTest}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-sm font-medium transition-colors"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <TestTube2 className="w-3.5 h-3.5" />}
          Έλεγχος
        </button>
        <button
          data-testid="quick-test-url-close"
          onClick={() => { setExpanded(false); setResult(null); setUrl(''); }}
          className="px-2 py-1.5 text-xs text-gray-400 hover:text-gray-200"
        >
          ✕
        </button>
      </div>
      {result && (
        <div
          data-testid="quick-test-url-result"
          className={`mt-2 flex items-start gap-2 text-xs rounded-md px-2.5 py-2 border ${
            result.ok
              ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
              : 'bg-red-500/10 border-red-500/30 text-red-300'
          }`}
        >
          {result.ok ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> : <XCircle className="w-4 h-4 mt-0.5 shrink-0" />}
          <div className="flex-1 min-w-0">
            {result.ok ? (
              <>
                <div className="font-semibold">URL έγκυρο — το stream απαντά</div>
                {result.contentType && <div className="text-[11px] opacity-80 mt-0.5">Content-Type: {result.contentType}</div>}
                {result.metadata?.title && <div className="text-[11px] opacity-80">Τίτλος: {result.metadata.title}</div>}
                {result.metadata?.artist && <div className="text-[11px] opacity-80">Καλλιτέχνης: {result.metadata.artist}</div>}
              </>
            ) : (
              <>
                <div className="font-semibold">URL μη έγκυρο</div>
                <div className="text-[11px] opacity-80 mt-0.5 break-all">{result.error}</div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickTestUrl;
